import { useEffect, useState } from 'react'
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom'
import { BrandMark } from '@/components/BrandLogo'
import { AppHeader } from '@/components/AppHeader'
import { Sidebar } from '@/components/Sidebar'
import { SiteLayout } from '@/components/landing/SiteLayout'
import { ConnectAccountPage } from '@/pages/ConnectAccountPage'
import { ConnectDevicePage } from '@/pages/ConnectDevicePage'
import { AuthCallbackPage } from '@/pages/AuthCallbackPage'
import { BlogPage } from '@/pages/BlogPage'
import { BlogPostPage } from '@/pages/BlogPostPage'
import { FaqPage } from '@/pages/FaqPage'
import { FeaturesPage } from '@/pages/FeaturesPage'
// Chat is not served from the website any more -- see the route below.
// import { ChatPage } from '@/pages/ChatPage'
import { ImagesPage } from '@/pages/ImagesPage'
import { OpenAppPage } from '@/pages/OpenAppPage'
import { DownloadPage } from '@/pages/DownloadPage'
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage'
import { KnowledgePage } from '@/pages/KnowledgePage'
import { LandingPage } from '@/pages/LandingPage'
import { LoginPage } from '@/pages/LoginPage'
import { ModelHubPage } from '@/pages/ModelHubPage'
import { RoadmapPage } from '@/pages/RoadmapPage'
import { WorkspacePage } from '@/pages/WorkspacePage'
import { AdminPage } from '@/pages/AdminPage'
import { ModelsPage } from '@/pages/ModelsPage'
import { ProjectsPage } from '@/pages/ProjectsPage'
import { ResetPasswordPage } from '@/pages/ResetPasswordPage'
import { SettingsPage } from '@/pages/SettingsPage'
import { isDesktopApp, SIGNED_IN_HOME } from '@/services/platform'
import { useAuth } from '@/store/auth'
// The store is untouched; nothing on the website reads it while chat is hidden.
// import { useChat } from '@/store/chat'

/**
 * The pages that describe the product.
 *
 * <p>Declared once because both trees serve them. Being signed in is no reason
 * to be refused the roadmap or the download page -- before this, every one of
 * these addresses fell through to the catch-all and bounced the reader into
 * /chat, so a link to them was broken for exactly the people most likely to
 * follow it.
 *
 * <p>The landing page is deliberately not in here. It sits at `/`, which for
 * someone already signed in is the way back into the application rather than a
 * pitch to install it.
 */
const MARKETING = [
  { path: '/features', element: <FeaturesPage /> },
  // The page was called Capabilities before the masthead was regrouped; an old
  // link should still land somewhere.
  { path: '/capabilities', element: <Navigate to="/features" replace /> },
  { path: '/model-hub', element: <ModelHubPage /> },
  { path: '/knowledge', element: <KnowledgePage /> },
  { path: '/workspace', element: <WorkspacePage /> },
  { path: '/roadmap', element: <RoadmapPage /> },
  { path: '/download', element: <DownloadPage /> },
  { path: '/faq', element: <FaqPage /> },
  { path: '/blog', element: <BlogPage /> },
  { path: '/blog/:slug', element: <BlogPostPage /> },
  { path: '/blog/category/:category', element: <BlogPage /> },
  { path: '/blog/category/:category/:slug', element: <BlogPostPage /> },
]

function marketingRoutes() {
  return MARKETING.map((route) => (
    <Route key={route.path} path={route.path} element={route.element} />
  ))
}

/** Sends an anonymous visitor to sign in, remembering where they were going. */
function SignInFirst() {
  const location = useLocation()
  const next = encodeURIComponent(location.pathname + location.search)
  return <Navigate to={`/login?next=${next}`} replace />
}

/**
 * Where to land once signed in.
 *
 * Signing in re-renders into the authenticated routes, where /login no longer
 * exists, so without this the catch-all sent everyone to /chat and discarded
 * wherever they were originally headed. Only paths are followed, so this
 * cannot bounce anyone off the site.
 */
function AfterSignIn() {
  const location = useLocation()
  const next = new URLSearchParams(location.search).get('next')
  // Without somewhere specific to be -- approving a device, most often -- the
  // destination is the desktop app rather than a page here. /open asks it to
  // come forward and falls through to the download when nothing does; inside
  // the app there is nothing to hand over to, so SIGNED_IN_HOME differs.
  return <Navigate to={next && next.startsWith('/') ? next : SIGNED_IN_HOME} replace />
}

export default function App() {
  const { status, restore } = useAuth()
  // Loaded for the sidebar's conversation list, which is hidden along with the
  // chat. Nothing on the website reads them now.
  // const loadConversations = useChat((s) => s.loadConversations)

  useEffect(() => {
    void restore()
  }, [restore])

  // useEffect(() => {
  //   if (status === 'authenticated') void loadConversations()
  // }, [status, loadConversations])

  if (status === 'loading') {
    // The first thing anyone sees on a cold start, and on the desktop app it
    // is the whole window for a moment -- so it carries the mark rather than a
    // bare word.
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4">
        <BrandMark className="h-16 w-16 animate-pulse" alt="" />
        <p className="text-sm text-content-muted">Loading…</p>
      </div>
    )
  }

  if (status === 'anonymous') {
    // The landing page exists to get people to install the app. Showing it
    // inside the installed app would ask the user to download what they are
    // already running, so the desktop shell goes straight to sign-in.
    if (isDesktopApp) {
      // No password is ever typed here: signing in happens in a browser the
      // person already trusts, and approving this machine there pairs the
      // device at the same time. The password-reset routes stay reachable
      // because a reset link opened on this machine may land in the app.
      return (
        <Routes>
          <Route path="/login" element={<ConnectAccountPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/auth/callback" element={<AuthCallbackPage />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      )
    }
    return (
      <Routes>
        {/* The marketing site. Every masthead entry is a page of its own, and
            they share one layout so the header and footer are identical on all
            of them. */}
        <Route element={<SiteLayout />}>
          <Route path="/" element={<LandingPage />} />
          {marketingRoutes()}
        </Route>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        {/*
          A device sent someone here to approve it. Signing in must come back
          to this exact URL: the code lives in it, and nobody can retype a code
          they never saw.
        */}
        <Route path="/connect" element={<SignInFirst />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    )
  }

  return (
    <Routes>
      {/* Same layout, same header and footer as a visitor sees. */}
      <Route element={<SiteLayout />}>{marketingRoutes()}</Route>

      {/* Everything else is the application. The splat keeps `/` and any
          unknown address landing in the chat, as before. */}
      {/*
        Handing over to the desktop app. Outside the shell on purpose: it is a
        moment in transit, not a page to sit on, and a sidebar around it would
        suggest otherwise.
      */}
      <Route path="/open" element={<OpenAppPage />} />

      <Route element={<AppShell />}>
        {/*
          Chat has moved to the desktop app, where the models actually run.
          The page and its store are untouched -- restoring this is these two
          lines, the import above, and the sidebar block in Sidebar.tsx.
        */}
        {/* <Route path="/chat" element={<ChatPage />} /> */}
        {/* <Route path="/chat/:id" element={<ChatPage />} /> */}
        <Route path="/chat/*" element={<Navigate to={SIGNED_IN_HOME} replace />} />
        <Route path="/models" element={<ModelsPage />} />
        <Route path="/images" element={<ImagesPage />} />
        <Route path="/projects" element={<ProjectsPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        {/* Routed for everyone. The page itself explains the refusal to
            someone without the role, and the server refuses the requests --
            a route that only exists for administrators would 404 for
            everyone else, which reads as a broken link rather than a
            permission. */}
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/connect" element={<ConnectDevicePage />} />
        {/* Signing in leaves the visitor on /login; honour where they were going. */}
        <Route path="/login" element={<AfterSignIn />} />
        <Route path="*" element={<Navigate to={SIGNED_IN_HOME} replace />} />
      </Route>
    </Routes>
  )
}

/**
 * The signed-in layout.
 *
 * <p>The navigation is a column of this layout on a wide screen and a drawer
 * over it on a narrow one, so a phone gets the whole width for the work rather
 * than 23% of it.
 */
function AppShell() {
  const [navOpen, setNavOpen] = useState(false)
  const { pathname } = useLocation()

  // Following a link inside the drawer should also put it away.
  useEffect(() => setNavOpen(false), [pathname])

  return (
    <div className="flex h-full">
      <Sidebar open={navOpen} onClose={() => setNavOpen(false)} />

      <div className="flex min-w-0 flex-1 flex-col">
        <AppHeader onOpenNav={() => setNavOpen(true)} />

        <main className="min-h-0 min-w-0 flex-1">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
